import React, { useMemo, useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useCategories } from "../../../../hooks/category/useCategories";
import { useCategoryListings } from "../../../../hooks/listing/useCategoryListings";
import { useSearch } from "../../../../hooks/listing/useSearch";
import { useDebounce } from "../../../../hooks/useDebounce";
import FilterPanel from "../../../../components/listing/categorydetail/FilterPanel";
import ProductToolbar from "../../../../components/listing/categorydetail/ProductToolbar";
import ProductGrid from "../../../../components/listing/product-card/ProductGrid";

const CategoryPage = () => {
  const params = useParams();
  const categoryIdParam = params.categoryID ?? params.id ?? params.CategoryID;
  const parentID = parseInt(categoryIdParam, 10);
  const isParentIdValid = Number.isInteger(parentID) && parentID > 0;

  // Hooks
  const {
    findCategoryById,
    findParentOf,
    loading: categoriesLoading,
  } = useCategories();

  // ✅ Dùng hook mới - chỉ load listings của category này
  // Giảm xuống 30 items để load nhanh hơn
  const {
    listings: categoryListings,
    loading: listingsLoading,
    hasMore,
    loadMore,
    totalCount,
  } = useCategoryListings(parentID, 30);

  // State cho filters
  const [activeSubCategoryID, setActiveSubCategoryID] = useState(null);
  const [activeProvinces, setActiveProvinces] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [condition, setCondition] = useState(null);
  const [sortBy, setSortBy] = useState("newest");
  const [searchText, setSearchText] = useState("");

  // State cho phân trang
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 20;

  const debouncedSearchText = useDebounce(searchText, 500);

  const searchCategoryID = activeSubCategoryID || parentID;
  const searchParentID = activeSubCategoryID
    ? parentID
    : findParentOf(parentID)?.CategoryID;

  const {
    searchedListings,
    searchLoading,
    searchHasMore,
    loadMoreSearchResults,
  } = useSearch(debouncedSearchText, searchCategoryID, searchParentID);

  // Lấy category hiện tại
  const currentCategory = findCategoryById(parentID);

  // Lấy subcategories từ children của category hiện tại
  const subCategories = currentCategory?.Children || [];

  // Logic lọc và sắp xếp
  const { filteredProducts, subCategoryCounts } = useMemo(() => {
    if (!currentCategory)
      return { filteredProducts: [], subCategoryCounts: {} };

    // ✅ Bỏ filter ParentID vì API đã filter rồi
    let filtered = [...categoryListings];

    const counts = {};
    subCategories.forEach((sub) => {
      counts[sub.CategoryID] = filtered.filter(
        (listing) => listing.CategoryID === sub.CategoryID
      ).length;
    });

    if (activeSubCategoryID) {
      filtered = filtered.filter(
        (listing) => listing.CategoryID === activeSubCategoryID
      );
    }

    if (activeProvinces.length > 0) {
      filtered = filtered.filter((listing) =>
        activeProvinces.includes(listing.ProvinceID)
      );
    }

    if (condition) {
      filtered = filtered.filter((listing) => {
        if (condition === "new")
          return listing.condition === "new" || listing.status === "new";
        if (condition === "used")
          return listing.condition === "used" || listing.status === "used";
        return true;
      });
    }

    if (priceRange.min || priceRange.max) {
      filtered = filtered.filter((listing) => {
        const price = listing.price || 0;
        const min = priceRange.min ? parseInt(priceRange.min) : 0;
        const max = priceRange.max ? parseInt(priceRange.max) : Infinity;
        return price >= min && price <= max;
      });
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "price-asc":
          return (a.price || 0) - (b.price || 0);
        case "price-desc":
          return (b.price || 0) - (a.price || 0);
        case "newest":
        default:
          return new Date(b.created_time || 0) - new Date(a.created_time || 0);
      }
    });

    return { filteredProducts: filtered, subCategoryCounts: counts };
  }, [
    categoryListings, // ✅ Thay displayedListings bằng categoryListings
    currentCategory,
    activeSubCategoryID,
    activeProvinces,
    priceRange,
    condition,
    sortBy,
    subCategories,
  ]);

  const productsToDisplay = debouncedSearchText
    ? searchedListings
    : filteredProducts;
  const currentHasMore = debouncedSearchText ? searchHasMore : hasMore;
  const currentLoadMore = debouncedSearchText
    ? loadMoreSearchResults
    : loadMore;

  const loading = listingsLoading || categoriesLoading || searchLoading;

  // Phân trang
  const totalPages = Math.ceil(productsToDisplay.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const paginatedProducts = productsToDisplay.slice(startIndex, endIndex);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    activeSubCategoryID,
    activeProvinces,
    priceRange,
    condition,
    sortBy,
    debouncedSearchText,
  ]);

  const resetFilters = () => {
    setActiveSubCategoryID(null);
    setActiveProvinces([]);
    setPriceRange({ min: "", max: "" });
    setCondition(null);
    setSearchText("");
    setCurrentPage(1);
  };

  const formatPrice = (price) => {
    if (!price) return "Liên hệ";
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(price);
  };

  if (!isParentIdValid) {
    return (
      <div className="error-page">
        <h2>Đường dẫn không hợp lệ</h2>
        <Link to="/market/thanh-ly" className="back-btn">
          Quay lại trang chủ
        </Link>
      </div>
    );
  }

  if (categoriesLoading) {
    return (
      <div className="loading-page">
        <div className="loading-spinner"></div>
        <p>Đang tải danh mục...</p>
      </div>
    );
  }

  if (!currentCategory) {
    return (
      <div className="error-page">
        <h2>Không tìm thấy danh mục</h2>
        <Link to="/market/thanh-ly" className="back-btn">
          Quay lại trang chủ
        </Link>
      </div>
    );
  }

  return (
    <div className="category-page">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/market" className="breadcrumb-link">
          Trang chủ
        </Link>
        <span className="breadcrumb-separator">/</span>
        <Link to="/market/thanh-ly" className="breadcrumb-link">
          Thanh lý
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          {currentCategory.CategoryName}
        </span>
      </div>

      {/* Filter Panel */}
      <FilterPanel
        category={currentCategory}
        activeSubCategoryID={activeSubCategoryID}
        onSubCategoryChange={setActiveSubCategoryID}
        activeProvinces={activeProvinces}
        onProvinceChange={setActiveProvinces}
        priceRange={priceRange}
        onPriceChange={setPriceRange}
        condition={condition}
        onConditionChange={setCondition}
        onResetFilters={resetFilters}
      />

      {/* Products List Panel */}
      <div className="products-panel">
        {/* Product Toolbar */}
        <ProductToolbar
          sortBy={sortBy}
          onSortChange={setSortBy}
          productsCount={productsToDisplay.length}
        />

        {/* Products Grid */}
        {listingsLoading && paginatedProducts.length === 0 ? (
          // Loading Skeleton
          <div className="products-grid-skeleton">
            {[...Array(12)].map((_, index) => (
              <div key={index} className="product-card-skeleton">
                <div className="skeleton-image"></div>
                <div className="skeleton-content">
                  <div className="skeleton-line skeleton-title"></div>
                  <div className="skeleton-line skeleton-price"></div>
                  <div className="skeleton-line skeleton-location"></div>
                </div>
              </div>
            ))}
          </div>
        ) : paginatedProducts.length > 0 ? (
          <>
            <ProductGrid listings={paginatedProducts} />

            {/* Loading More Indicator */}
            {listingsLoading && paginatedProducts.length > 0 && (
              <div className="loading-more">
                <div className="loading-spinner-small"></div>
                <span>Đang tải thêm sản phẩm...</span>
              </div>
            )}
          </>
        ) : (
          <div className="no-results">
            <div className="no-results-icon">🔍</div>
            <h3>Không tìm thấy sản phẩm</h3>
            <p>Thử điều chỉnh bộ lọc hoặc từ khóa tìm kiếm</p>
            <button onClick={resetFilters} className="reset-filters-btn">
              Xóa bộ lọc
            </button>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              « Trước
            </button>

            {[...Array(totalPages)].map((_, index) => {
              const pageNum = index + 1;
              // Show first, last, current, and adjacent pages
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
              ) {
                return (
                  <button
                    key={pageNum}
                    className={`pagination-btn ${
                      currentPage === pageNum ? "active" : ""
                    }`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              } else if (
                pageNum === currentPage - 3 ||
                pageNum === currentPage + 3
              ) {
                return (
                  <span key={pageNum} className="pagination-dots">
                    ...
                  </span>
                );
              }
              return null;
            })}

            <button
              className="pagination-btn"
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
            >
              Sau »
            </button>
          </div>
        )}
      </div>

      <style jsx>{`
        .category-page {
          min-height: calc(100vh - 60px);
          background-color: #0f172a;
          color: #e5e7eb;
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .breadcrumb {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 24px;
          font-size: 14px;
        }

        .breadcrumb-link {
          color: #4ade80;
          text-decoration: none;
          transition: color 0.2s;
        }

        .breadcrumb-link:hover {
          color: #22c55e;
        }

        .breadcrumb-separator {
          color: #64748b;
        }

        .breadcrumb-current {
          color: #cbd5e1;
        }

        /* Products Panel */}
        .products-panel {
          background: #1e293b;
          border-radius: 16px;
          padding: 24px;
          border: 1px solid #334155;
          margin-top: 24px;
        }

        .no-results {
          text-align: center;
          padding: 60px 20px;
        }

        .no-results-icon {
          font-size: 4rem;
          margin-bottom: 16px;
        }

        .no-results h3 {
          font-size: 1.5rem;
          color: #f1f5f9;
          margin-bottom: 8px;
        }

        .no-results p {
          color: #cbd5e1;
          margin-bottom: 24px;
        }

        .reset-filters-btn {
          background: #4ade80;
          color: #0f172a;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s;
        }

        .reset-filters-btn:hover {
          background: #22c55e;
        }

        /* Loading Skeleton */
        .products-grid-skeleton {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 20px;
          margin-top: 20px;
        }

        .product-card-skeleton {
          background: #1e293b;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid #334155;
        }

        .skeleton-image {
          width: 100%;
          height: 200px;
          background: linear-gradient(
            90deg,
            #1e293b 0%,
            #334155 50%,
            #1e293b 100%
          );
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }

        .skeleton-content {
          padding: 16px;
        }

        .skeleton-line {
          height: 16px;
          background: linear-gradient(
            90deg,
            #1e293b 0%,
            #334155 50%,
            #1e293b 100%
          );
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 4px;
          margin-bottom: 12px;
        }

        .skeleton-title {
          width: 80%;
          height: 20px;
        }

        .skeleton-price {
          width: 60%;
          height: 18px;
        }

        .skeleton-location {
          width: 50%;
          height: 14px;
        }

        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }

        /* Loading More Indicator */
        .loading-more {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 24px;
          margin-top: 24px;
          color: #94a3b8;
          font-size: 14px;
        }

        .loading-spinner-small {
          width: 20px;
          height: 20px;
          border: 3px solid #334155;
          border-top: 3px solid #4ade80;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        /* Pagination */
        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          margin-top: 40px;
          padding-top: 32px;
          border-top: 1px solid #334155;
        }

        .pagination-btn {
          background: transparent;
          border: 1px solid #475569;
          color: #cbd5e1;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 14px;
          min-width: 40px;
        }

        .pagination-btn:hover:not(:disabled) {
          border-color: #4ade80;
          color: #4ade80;
        }

        .pagination-btn.active {
          background: #4ade80;
          color: #0f172a;
          border-color: #4ade80;
          font-weight: 600;
        }

        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-dots {
          color: #64748b;
          padding: 0 4px;
        }

        .error-page, .loading-page {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          text-align: center;
        }

        .error-page h2 {
          color: #f1f5f9;
          margin-bottom: 16px;
        }

        .back-btn {
          background: #4ade80;
          color: #0f172a;
          padding: 12px 24px;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #334155;
          border-top: 4px solid #4ade80;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .category-page {
            padding: 12px;
          }

          .breadcrumb {
            font-size: 12px;
          }

          .pagination {
            flex-wrap: wrap;
            gap: 6px;
          }

          .pagination-btn {
            padding: 6px 12px;
            font-size: 13px;
            min-width: 36px;
          }
        }
      `}</style>
    </div>
  );
};

export default CategoryPage;
